import { theme } from 'antd';
import { createStyles, FullToken } from 'antd-style';
import React, { useEffect, useRef } from 'react';
import TranslatedTable, { TranslatedTableProps } from './TranslatedTable';

const useFlexTableStyle = createStyles(
  ({ token, css }, props: { flexAuto?: boolean; otherToken: FullToken }) => {
    return {
      flexTableStyle: {
        ...(props.flexAuto ? { flex: 'auto' } : { minHeight: 0, height: '100%' }),
        minWidth: 0,

        '> .ant-table-wrapper': {
          height: '100%',
          padding: '0 !important',
          backgroundColor: props.otherToken.colorBgContainer,
          borderRadius: props.otherToken.borderRadiusLG,
          overflow: 'hidden',

          '> .ant-spin-nested-loading': {
            height: '100%'
          },

          '& > .ant-spin-nested-loading > .ant-spin-container': {
            height: '100%',
            display: 'flex',
            flexDirection: 'column',

            '> .ant-table': {
              flex: 1,
              height: 0,
              display: 'flex',
              flexDirection: 'column',

              '> .ant-table-container': {
                height: 0,
                flex: 1,
                display: 'flex',
                flexDirection: 'column',

                '> .ant-table-body': {
                  height: 0,
                  flex: 2,
                  overflowY: 'auto'
                }
              },

              '> .ant-table-footer': {
                backgroundColor: 'var(--layout-bg-color)'
              }
            }
          },
          '& .ant-spin-container > .ant-pagination': {
            paddingRight: '16px'
          },
          '& .ant-table > .ant-table-footer': {
            paddingRight: '16px'
          }
        }
      }
    };
  }
);

export type FlexTableProps<T> = TranslatedTableProps<T> & {
  flexAuto?: boolean;
  initialScroll?: number;
  onScroll?: (offset: number) => void;
  onScrollNearBottom?: () => void;
  hideScrollOnPagination?:boolean;
};

/*
 * Wrapper around TranslatedTable component that makes the table expand to fill the available space while still being bound by the parent container.
 * Normally, this will result in more rows than can currently be shown on the screen, so the table body will scroll to show all content.
 * Note: This is liable to break if we upgrade AntD library to a new major version, so be prepared to fix
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default function FlexTable<T extends object = any>(
  props: FlexTableProps<T>
): React.ReactElement {
  const { flexAuto, onScrollNearBottom, onScroll, initialScroll, className, ...tableProps } = props;
  const { token } = theme.useToken();

  // Passing the token in here manually because the token argument doesn't seem to use the theme token.
  const { cx, styles } = useFlexTableStyle({ flexAuto, otherToken: token });

  const ref = useRef<HTMLDivElement>(null);
  const isNearBottom = useRef(false);

  useEffect(() => {
    if (ref.current && tableProps.dataSource && tableProps.dataSource.length > 0) {
      const body = ref.current.querySelector('.ant-table-body') as HTMLDivElement;
      if (body && body.scrollTop === 0 && initialScroll) {
        body.scrollTop = initialScroll;
      }
    }
  }, [ref, tableProps.dataSource, initialScroll]);

  useEffect(() => {
    if (ref.current) {
      const body = ref.current.querySelector('.ant-table-body') as HTMLDivElement;
      if (body) {
        const handleScroll = () => {
          onScroll && onScroll(body.scrollTop);
          if (onScrollNearBottom) {
            const nearBottom = body.scrollTop + body.offsetHeight > body.scrollHeight - 100;
            if (nearBottom !== isNearBottom.current) {
              isNearBottom.current = nearBottom;
              if (nearBottom) onScrollNearBottom();
            }
          }
        };
        body.addEventListener('scroll', handleScroll);
        return () => body.removeEventListener('scroll', handleScroll);
      }
    }
  }, [isNearBottom, ref, onScrollNearBottom, onScroll]);

  return (
    <div ref={ref} className={`${className} ${cx(styles.flexTableStyle)}`}>
      <TranslatedTable<T> {...tableProps} scroll={props.hideScrollOnPagination ? undefined : { y: '100%' } } />
    </div>
  );
}
